import React, { useCallback, useEffect, useState } from "react";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import { useShopEvent } from "../../hooks/useShopEvent";
import { daysFromToday, formatTime, relativeDay } from "../../utils/dates";
import { formatMoney } from "../utils/money";

/**
 * Orders waiting to be collected.
 *
 * Lives on the register because that is where the handover happens: a customer
 * walks in saying "I ordered something", and nobody should have to go to the
 * admin area to find it.
 *
 * Orders due today or already late are highlighted, and the list refreshes by
 * itself when an order is placed or changes, so it is right the moment it is
 * opened from a "new pre-order" alert.
 *
 * Handing one over decrements the stock, which is why it asks first — the
 * items are physically going out of the door.
 */
const PAYMENT_METHODS = ["cash", "card", "mobile_payment"];

export default function PickupsSheet({ currency, onClose, onCollected }) {
  const { t } = useI18n();
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(null);
  // The customer pays at pickup, and the handover records that sale
  const [method, setMethod] = useState("cash");
  const [error, setError] = useState("");
  const [markingReady, setMarkingReady] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await posAPI.pendingPickups();
      setOrders(data);
      setError("");
    } catch {
      setError(t("pos.pickups.loadFailed"));
      setOrders((current) => current || []);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useShopEvent("preorder:created", load);
  useShopEvent("preorder:updated", load);

  const collect = async (order) => {
    setBusy(order.id);
    setError("");

    try {
      await posAPI.collectPreOrder(order.id, method);
      setConfirming(null);
      await load();
      onCollected?.();
    } catch (err) {
      setError(err.response?.data?.message || t("pos.pickups.collectFailed"));
    } finally {
      setBusy(null);
    }
  };

  const markReady = async (order) => {
    setMarkingReady(order.id);
    setError("");

    try {
      await posAPI.markPreOrderReady(order.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("pos.pickups.markReadyFailed"));
    } finally {
      setMarkingReady(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 text-slate-100 flex items-end sm:items-center justify-center">
      <div className="w-full sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-slate-900 border border-slate-700 p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-4">
          <p className="text-slate-100 font-semibold">
            {t("pos.pickups.title")}
          </p>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="h-9 w-9 rounded-lg bg-slate-800 text-slate-300"
          >
            ✕
          </button>
        </div>

        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}

        {orders === null ? (
          <p className="py-8 text-center text-slate-400">
            {t("common.loading")}
          </p>
        ) : orders.length === 0 ? (
          <p className="py-8 text-center text-slate-400">
            {t("pos.pickups.empty")}
          </p>
        ) : (
          <ul className="space-y-3">
            {orders.map((order) => {
              const diff = daysFromToday(order.pickup_date);
              const late = diff !== null && diff < 0;
              const dueToday = diff === 0;

              return (
                <li
                  key={order.id}
                  className={`rounded-xl border p-4 ${
                    late
                      ? "bg-red-500/10 border-red-500/50"
                      : dueToday
                        ? "bg-amber-500/10 border-amber-500/60"
                        : "bg-slate-800/70 border-slate-700"
                  }`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-semibold text-slate-100">
                      {order.user_name || t("pos.pickups.customer")}
                    </p>
                    <span className="font-bold text-emerald-400">
                      {formatMoney(order.total_amount, currency)}
                    </span>
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${
                        order.status === "ready"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-slate-700 text-slate-300"
                      }`}
                    >
                      {order.status === "ready"
                        ? t("pos.pickups.statusReady")
                        : t("pos.pickups.statusPreparing")}
                    </span>

                    {(late || dueToday) && (
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${
                          late
                            ? "bg-red-500 text-white"
                            : "bg-amber-500 text-slate-900"
                        }`}
                      >
                        {late
                          ? t("pos.pickups.late", { count: Math.abs(diff) })
                          : t("pos.pickups.dueToday")}
                      </span>
                    )}
                  </div>

                  <p className="mt-1 text-sm text-slate-400">
                    {t("pos.pickups.at", {
                      day: relativeDay(order.pickup_date),
                      time: formatTime(order.pickup_time),
                    })}
                    {order.user_phone && (
                      <>
                        {" · "}
                        <a
                          href={`tel:${order.user_phone}`}
                          dir="ltr"
                          className="text-primary-400 hover:underline"
                        >
                          {order.user_phone}
                        </a>
                      </>
                    )}
                  </p>

                  <ul className="mt-2 text-sm text-slate-300">
                    {order.items.map((item) => (
                      <li key={item.id}>
                        {item.quantity} × {item.product_name}
                      </li>
                    ))}
                  </ul>

                  {confirming === order.id ? (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-slate-400">
                        {t("pos.pickups.paidBy")}
                      </p>
                      <div
                        role="group"
                        aria-label={t("pos.pickups.paidBy")}
                        className="grid grid-cols-3 gap-2"
                      >
                        {PAYMENT_METHODS.map((id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setMethod(id)}
                            aria-pressed={method === id}
                            className={`h-10 rounded-lg border text-sm font-semibold ${
                              method === id
                                ? "bg-primary-600 border-primary-500 text-white"
                                : "bg-slate-800 border-slate-600 text-slate-300"
                            }`}
                          >
                            {t(`common.payment.${id}`)}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-300 flex-1">
                          {t("pos.pickups.confirm", {
                            amount: formatMoney(order.total_amount, currency),
                          })}
                        </span>
                        <button
                          onClick={() => collect(order)}
                          disabled={busy === order.id}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
                        >
                          {busy === order.id ? "…" : t("pos.pickups.handedOver")}
                        </button>
                        <button
                          onClick={() => setConfirming(null)}
                          className="px-3 py-1.5 rounded-lg border border-slate-600 text-slate-300 text-sm"
                        >
                          {t("pos.pickups.notYet")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex gap-2">
                      {order.status !== "ready" && (
                        <button
                          onClick={() => markReady(order)}
                          disabled={markingReady === order.id}
                          className="h-10 flex-1 rounded-lg border border-emerald-600 text-emerald-400 font-semibold text-sm disabled:opacity-60"
                        >
                          {markingReady === order.id
                            ? "…"
                            : t("pos.pickups.markReady")}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setMethod("cash");
                          setConfirming(order.id);
                        }}
                        className="h-10 flex-1 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm"
                      >
                        {t("pos.pickups.handOver")}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
